<script setup lang="ts">
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { computed, onMounted, ref, watch } from 'vue'
  import { useUserStore } from '@/shared/stores/userStore'
  import {
    type WebhooksSettings,
    type WebhooksSettingsRequest,
    type CustomerUser,
    Role5ccEnum,
    SettingsService
  } from '@/types/generated'
  import * as Sentry from '@sentry/vue'

  const isLoading = ref(false)
  const { fetchUser } = useUserStore()
  const user = computed((): CustomerUser => useUserStore().user as CustomerUser)

  // Webhooks integration fields
  const isWebhooksEnabled = ref(false)
  const webhookUrls = ref('')

  const { t } = useTranslation({
    en: {
      warning: 'Warning',
      onlyAdminsAllowedMsg: 'Only the administrator is allowed to edit this setting.',
      description:
        'Configure webhook integration settings. Webhooks allow JUPUS to send data to external URLs when certain events occur.',
      enableWebhooks: 'Enable Webhooks Integration',
      webhookUrls: 'Webhook URLs',
      webhookUrlsPlaceholder: 'Enter webhook URLs (comma-separated)',
      webhookUrlsHelper: 'Enter one or more webhook URLs separated by commas'
    },
    de: {
      warning: 'Achtung',
      onlyAdminsAllowedMsg: 'Nur der Administrator kann diese Einstellung bearbeiten.',
      description:
        'Konfigurieren Sie die Webhook-Integrationseinstellungen. Webhooks ermöglichen es JUPUS, Daten an externe URLs zu senden, wenn bestimmte Ereignisse eintreten.',
      enableWebhooks: 'Webhooks Integration aktivieren',
      webhookUrls: 'Webhook URLs',
      webhookUrlsPlaceholder: 'Webhook URLs eingeben (kommagetrennt)',
      webhookUrlsHelper: 'Geben Sie eine oder mehrere Webhook-URLs ein, getrennt durch Kommas'
    }
  })

  const updateWebhooksSettings = async () => {
    try {
      isLoading.value = true

      const data: WebhooksSettingsRequest = {
        is_webhooks_egress_integration_enabled: isWebhooksEnabled.value
      }

      if (webhookUrls.value) {
        data.webhook_urls = webhookUrls.value
      }

      await SettingsService.settingsWebhooksCreate({
        requestBody: data
      })
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      isLoading.value = false
    }
  }

  onMounted(async () => {
    if (!user.value) {
      await fetchUser()
    }

    try {
      const settings = (await SettingsService.settingsWebhooksList()) as WebhooksSettings

      if (settings) {
        isWebhooksEnabled.value = settings.is_webhooks_egress_integration_enabled || false
        webhookUrls.value = settings.webhook_urls || ''
      }
    } catch (error) {
      console.error('Error loading Webhooks settings:', error)
      Sentry.captureException(error)
    }
  })

  watch([isWebhooksEnabled], () => {
    if (user.value) {
      updateWebhooksSettings()
    }
  })

  watch(webhookUrls, () => {
    if (user.value) {
      updateWebhooksSettings()
    }
  })
</script>

<template>
  <SettingsPage v-if="user">
    <div class="space-y-8">
      <div
        v-if="user.role !== Role5ccEnum.ADMIN"
        class="mb-6"
      >
        <div class="mb-4 w-full rounded-md bg-orange-100 p-4 text-orange-700">
          <p class="font-medium">{{ t('warning') }}</p>
          <p class="mt-2">{{ t('onlyAdminsAllowedMsg') }}</p>
        </div>
      </div>
      <div
        v-else
        class="mb-6"
      >
        <p class="mb-4 text-sm text-gray-600">{{ t('description') }}</p>

        <div class="max-w-xl space-y-4">
          <CheckboxField
            v-model="isWebhooksEnabled"
            name="is_webhooks_egress_integration_enabled"
            :label="t('enableWebhooks')"
          />

          <div v-if="isWebhooksEnabled">
            <label
              for="webhook_urls"
              class="mb-1 block text-sm font-medium text-gray-700"
            >
              {{ t('webhookUrls') }}
            </label>
            <textarea
              id="webhook_urls"
              v-model="webhookUrls"
              :placeholder="t('webhookUrlsPlaceholder')"
              rows="4"
              class="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500
                sm:text-sm"
            />
            <p class="mt-1 text-xs text-gray-500">{{ t('webhookUrlsHelper') }}</p>
          </div>
        </div>
      </div>
    </div>
  </SettingsPage>
</template>

<style scoped>
  /* Add any scoped styles here if needed */
</style>
